// "Sync now" (ARCHITECTURE §6): queues a sync for the worker and follows the job until it ends or
// is stopped. With scheduled sync off, this button is how stored data gets current.
import { useEffect, useRef, useState } from "react";
import { api, type Job, type JobProgress } from "./api";

export type SyncNowState = { kind: "idle" | "running" | "done" | "stopped" } | { kind: "failed"; message: string };

/**
 * `path`: the job to start; "/admin/kurs" for the Currency tab's KMK rate update. `body`: what to
 * sync, e.g. the Sync button's `{ scope }` (the active page's part).
 */
export function useSyncNow(onDone: () => void, path = "/sync", body: object = {}) {
  const [state, setState] = useState<SyncNowState>({ kind: "idle" });
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const timer = useRef<ReturnType<typeof setInterval>>();
  const jobId = useRef<number | null>(null);
  useEffect(() => () => clearInterval(timer.current), []);

  async function start() {
    setState({ kind: "running" });
    setProgress(null);
    clearInterval(timer.current);
    jobId.current = null;
    let job: Job;
    try {
      job = await api<Job>(path, { method: "POST", body: JSON.stringify(body) });
    } catch (e) {
      return setState({ kind: "failed", message: e instanceof Error ? e.message : "Couldn't start the sync." });
    }
    jobId.current = job.id;
    const started = Date.now();
    timer.current = setInterval(async () => {
      let j: Job;
      try {
        j = await api<Job>(`/refresh/${job.id}`, { background: true });
      } catch {
        return;
      }
      setProgress(j.progress ?? null);
      const elapsed = Date.now() - started;
      if (j.state === "done") {
        clearInterval(timer.current);
        setState({ kind: "done" });
        onDone();
      } else if (j.state === "failed") {
        clearInterval(timer.current);
        setState({ kind: "failed", message: j.error ?? "Sync failed." });
        onDone();
      } else if (j.state === "cancelled") {
        // Stopped, here or by someone following the same sync: what was written so far shows.
        clearInterval(timer.current);
        setState({ kind: "stopped" });
        onDone();
      } else if (j.state === "queued" && elapsed > 10_000) {
        clearInterval(timer.current);
        setState({ kind: "failed", message: "The sync service is not running." });
      }
    }, 1000);
  }

  /** Stop: the sync ends now; the worker stops at its next step, keeping what it already wrote. */
  async function stop() {
    const id = jobId.current;
    if (id === null) return;
    clearInterval(timer.current);
    try {
      await api(`/sync/${id}/cancel`, { method: "POST", body: "{}" });
    } catch {
      // it ended meanwhile; the last poll's state stands
    }
    setState({ kind: "stopped" });
    onDone();
  }

  return { state, progress, start, stop, busy: state.kind === "running" };
}
